import { createAsyncThunk, createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";
import { AppDataState, AppId, AppThunkAPIType, unknownError } from "../../app/appTypes";
import { RootState } from "../../app/store";
import { getWithAuth, postWithAuth, putWithAuth, deleteWithAuthAndBody } from "../../http"

export interface MailTemplate {
    id: AppId,
    type: string,
    context: string,
    body: string,
    bodyEN: string,
    subject: string,
    subjectEN: string,
    footerId: string | null,
    companyId: number
    isFixed: boolean
}

export type DraftMailTemplate = Omit<MailTemplate, "id" | "companyId" | "isFixed">;
export type MailTemplatePayload = Omit<MailTemplate, "footerId" | "companyId" | "isFixed"> & { reason: string }

export interface ExtendedMailTemplate extends MailTemplate {
    originalId: AppId;
}

export interface DeleteMailTemplateResult {
    originalId: AppId;
    template: MailTemplate | null;
}

const adapter = createEntityAdapter<MailTemplate>({
    selectId: (mailTemplate) => mailTemplate.id,
})

export type MailTemplatesState = EntityState<MailTemplate> & { state: AppDataState }

const initialState: MailTemplatesState = adapter.getInitialState({
    state: { type: "empty" },
})

export const loadMailTemplates = createAsyncThunk<MailTemplate[], void, AppThunkAPIType>("mailTemplates/load", async (filter, { getState, requestId, dispatch, rejectWithValue }) => {
    const result = await dispatch(getWithAuth({ url: `api/mail-templates` }))
    const { payload } = result

    if (getWithAuth.fulfilled.match(result)) {
        return payload as MailTemplate[]
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }

})

export const createNewCompanyMailTemplate = createAsyncThunk<ExtendedMailTemplate, MailTemplatePayload, AppThunkAPIType>(
    "mailTemplates/new-company-mail-template",
    async (mailTemplate, { getState, requestId, dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ url: "api/mail-templates", payload: mailTemplate }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            payload.originalId = mailTemplate.id;
            return payload as ExtendedMailTemplate
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateCompanyMailTemplate = createAsyncThunk<MailTemplate, MailTemplatePayload, AppThunkAPIType>(
    "mailTemplate/edit",
    async (mailTemplate, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({ url: `api/mail-templates/${mailTemplate.id}`, payload: mailTemplate }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as MailTemplate
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const deleteMailTemplate = createAsyncThunk<DeleteMailTemplateResult, { id: AppId, reason: string }, AppThunkAPIType>(
    "mailTemplate/delete",
    async ({ id, reason }, { rejectWithValue, dispatch }) => {
        const result = await dispatch(deleteWithAuthAndBody({
            url: `api/mail-templates/${id}`,
            payload: { reason },
        }))
        const { payload } = result
        if (deleteWithAuthAndBody.fulfilled.match(result)) {
            const template = payload && typeof payload === "object" && "id" in payload
                ? payload as MailTemplate
                : null
            return { originalId: id, template }
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const mailTemplatesSlice = createSlice({
    name: "mailTemplates",
    initialState,
    reducers: {
        clearRequirementsTypesSlice: (state) => {
            adapter.removeAll(state)
            state.state = { type: "empty" }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(createNewCompanyMailTemplate.fulfilled, (state, action) => {
            const { originalId } = action.payload
            const changes: Omit<MailTemplate, "id"> = action.payload
            state = adapter.updateOne(state, {
                id: originalId,
                changes,
            });
        });

        builder.addCase(updateCompanyMailTemplate.fulfilled, (state, action) => {
            const { id } = action.payload
            const changes: Omit<MailTemplate, "id"> = action.payload
            state = adapter.updateOne(state, {
                id,
                changes,
            });
        });

        builder.addCase(deleteMailTemplate.fulfilled, (state, action) => {
            const { originalId, template } = action.payload

            if (template) {
                adapter.updateOne(state, {
                    id: originalId,
                    changes: template,
                });
            } else {
                adapter.removeOne(state, originalId)
            }
        });


        builder.addCase(loadMailTemplates.rejected, (state, action) => {
            if (state.state.type === "loading" && state.state.requestId === action.meta.requestId) {
                state.state = {
                    type: "error",
                    error: action.payload ?? unknownError(),
                }
            }
        })

        builder.addCase(loadMailTemplates.pending, (state, action) => {
            if (state.state.type === "empty" || state.state.type === "loaded") {
                state.state = {
                    type: "loading",
                    requestId: action.meta.requestId,
                }
            }
        })


        builder.addCase(loadMailTemplates.fulfilled, (state, action) => {
            if (state.state.type === "loading" && state.state.requestId === action.meta.requestId) {
                adapter.setAll(state, action.payload)
                state.state = { type: "loaded" }
            }
        })
    },
})

export const selectMailTemplates = (state: RootState): MailTemplatesState => state.mailTemplates

export const
    { selectAll: selectAllMailTemplates
        , selectById: selectMailTemplateById
        , selectTotal: selectTotalMailTemplates
        , selectEntities: selectMailTemplatesEntities
    } = adapter.getSelectors<RootState>(selectMailTemplates)

export default mailTemplatesSlice.reducer;
